<script setup lang="ts">
import { IconAlertCircle } from '@tabler/icons-vue';
import { ref, watch } from 'vue';

import { useAuthenticationAdapter } from '../adapter.ts';
import type { AuthenticationPurpose, AuthenticationResult } from '../api.ts';
import type { AuthenticationDestination, AuthenticationStart } from '../types.ts';
import AuthEntryFlow from './AuthEntryFlow.vue';
import AuthLinkFlow from './AuthLinkFlow.vue';
import AuthPasswordRecoveryFlow from './AuthPasswordRecoveryFlow.vue';
import AuthTwoFactorFlow from './AuthTwoFactorFlow.vue';

type AuthFlowScreen =
    | AuthenticationStart
    | { screen: 'two-factor'; challenge: string; purpose: AuthenticationPurpose }
    | { screen: 'expired'; title: string; detail: string };

const props = defineProps<{
    start: AuthenticationStart;
    intended?: string;
    autofocus?: boolean;
}>();

const emit = defineEmits<{
    complete: [redirect?: string];
    navigate: [destination: AuthenticationDestination, transition?: 'next' | 'prev'];
}>();

const current = ref<AuthFlowScreen>(props.start);
const completing = ref(false);
const authenticationAdapter = useAuthenticationAdapter();

watch(
    () => props.start,
    (start) => {
        current.value = start;
        completing.value = false;
    },
);

async function resolve(result: AuthenticationResult) {
    if (result.state === 'two-factor') {
        current.value = {
            screen: 'two-factor',
            challenge: result.challenge,
            purpose: result.purpose,
        };
        return;
    }

    completing.value = true;

    try {
        const completion = await authenticationAdapter.complete(result.grant, result.purpose);
        emit('complete', completion.redirect || undefined);
    } catch (reason) {
        completing.value = false;
        throw reason;
    }
}

function expire(title: string, detail: string) {
    current.value = { screen: 'expired', title, detail };
}

function restart() {
    if (props.start.screen === 'entry') {
        current.value = props.start;
        return;
    }

    emit('navigate', 'login');
}

function returnToEntry() {
    if (props.start.screen === 'entry') {
        current.value = props.start;
        return;
    }

    emit('navigate', 'login', 'prev');
}
</script>

<template>
    <div
        class="stack gap-xl"
        :class="{
            grow:
                current.screen === 'expired' ||
                (current.screen === 'authentication-link' && current.consumeImmediately),
        }"
    >
        <div v-if="current.screen === 'expired'" class="placeholder grow justify-center m-0 p-0">
            <IconAlertCircle class="placeholder__icon" />
            <h2 class="color-text">{{ current.title }}</h2>
            <p>{{ current.detail }}</p>
            <button class="btn btn--lg bg-primary full-width mt-lg" @click="restart">
                Try Again
            </button>
        </div>

        <AuthEntryFlow
            v-else-if="current.screen === 'entry'"
            :key="current.mode"
            :autofocus="autofocus"
            :completing="completing"
            :intended="intended"
            :resolve="resolve"
            :start="current"
            @navigate="emit('navigate', $event)"
        />

        <AuthPasswordRecoveryFlow
            v-else-if="current.screen === 'password-recovery'"
            :show-back="current.showBack !== false"
            @back="returnToEntry"
        />

        <AuthLinkFlow
            v-else-if="current.screen === 'authentication-link'"
            :key="current.token"
            :completing="completing"
            :resolve="resolve"
            :start="current"
            @expired="expire"
        />

        <AuthTwoFactorFlow
            v-else-if="current.screen === 'two-factor'"
            :challenge="current.challenge"
            :completing="completing"
            :purpose="current.purpose"
            :resolve="resolve"
            @back="returnToEntry"
            @expired="expire"
        />
    </div>
</template>
